import React, { useEffect, useState } from "react";
import { liff } from "@line/liff";
import RootLayout from "layouts/RootLayout";
import { Dialog, DialogTitle } from "@mui/material";
import CreatableSelect from "react-select/creatable";
import { SubmitButton } from "../components/SubmitButton";
import { getClinicList } from "apis/clinicService";
import { getActivityStatus, submitActivity } from "apis/activityService";
import type { ActivityPediatricianStatus } from "../types/activity";
import "./style.css";

const LIFF_ID = process.env.REACT_APP_ACTIVITY_LIFF_ID;

type ClinicOption = { value: string; label: string };
type ClinicInfo = { code: string; name: string; isManual: boolean } | null;
type Identity = "" | "孕期中" | "家長" | "其他";
// 是否已加入：joined / not_joined / unsure（未加入或不確定再問意願）
type JoinAnswer = "" | "joined" | "not_joined" | "unsure";
type WillAnswer = "" | "interested" | "not_interested";

const initializeLIFF = async () => {
    try {
        await liff.init({ liffId: LIFF_ID });
        if (!liff.isLoggedIn()) {
            liff.login();
        }
        return liff;
    } catch (error: unknown) {
        console.error("初始化 liff 時出錯：", error);
    }
};

// 前端壓縮圖片（長邊 ≤1280、JPEG 品質 ~0.7）→ data URL
const compressImage = (file: File): Promise<{ dataUrl: string; mime: string }> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const img = new Image();
            img.onload = () => {
                let { width, height } = img;
                const maxEdge = 1280;
                if (Math.max(width, height) > maxEdge) {
                    const scale = maxEdge / Math.max(width, height);
                    width = Math.round(width * scale);
                    height = Math.round(height * scale);
                }
                const canvas = document.createElement("canvas");
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext("2d");
                if (!ctx) return reject(new Error("canvas 不支援"));
                ctx.drawImage(img, 0, 0, width, height);
                resolve({ dataUrl: canvas.toDataURL("image/jpeg", 0.7), mime: "image/jpeg" });
            };
            img.onerror = reject;
            img.src = reader.result as string;
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });

const Activity = (): JSX.Element => {
    const [liffClientObject, setLiffClientObject] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false); // 已填寫完成（含本次送出成功 / 既有已填）

    const [identity, setIdentity] = useState<Identity>("");
    const [dueYear, setDueYear] = useState("");
    const [dueMonth, setDueMonth] = useState("");
    const [babyYear, setBabyYear] = useState("");
    const [babyMonth, setBabyMonth] = useState("");

    const [joinAnswer, setJoinAnswer] = useState<JoinAnswer>("");
    const [willAnswer, setWillAnswer] = useState<WillAnswer>("");
    const [clinic, setClinic] = useState<ClinicInfo>(null);
    const [childCareClinics, setChildCareClinics] = useState<ClinicOption[]>([]);

    const [proofPreview, setProofPreview] = useState<string>("");
    const [proofImageBase64, setProofImageBase64] = useState<string>("");
    const [proofImageMime, setProofImageMime] = useState<string>("");

    const accessToken = liffClientObject?.getAccessToken();

    useEffect(() => {
        const initialize = async () => {
            const liffObject = await initializeLIFF();
            setLiffClientObject(liffObject);
        };
        initialize();
    }, []);

    // 進頁面查是否已填（已填→顯示完成畫面）
    useEffect(() => {
        if (accessToken === undefined) return;
        getActivityStatus(accessToken)
            .then((res) => {
                if (res?.data?.submitted) setDone(true);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [accessToken]);

    // 幼兒專責機構診所清單（重用就醫資訊 API，篩 isChildCare）
    useEffect(() => {
        getClinicList()
            .then((list) => {
                const options: ClinicOption[] = (Array.isArray(list) ? list : [])
                    .filter((c) => c && c.isChildCare)
                    .map((c) => ({ value: c.code, label: c.name }));
                setChildCareClinics(options);
            })
            .catch(() => setChildCareClinics([]));
    }, []);

    const handleJoin = (v: JoinAnswer) => {
        setJoinAnswer(v);
        if (v === "joined") {
            setWillAnswer("");
        } else {
            setClinic(null);
        }
    };

    const handleClinicChange = (option: any, meta: any) => {
        if (!option) {
            setClinic(null);
        } else if (meta && meta.action === "create-option") {
            setClinic({ code: "", name: option.label, isManual: true });
        } else {
            setClinic({ code: option.value, name: option.label, isManual: false });
        }
    };

    const handleImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            alert("請上傳圖片檔");
            return;
        }
        try {
            const { dataUrl, mime } = await compressImage(file);
            setProofPreview(dataUrl);
            setProofImageBase64(dataUrl);
            setProofImageMime(mime);
        } catch {
            alert("圖片處理失敗，請換一張試試");
        }
    };

    // 由作答推導幼兒專責狀態；所有身分皆需填寫。
    const derivePediatricianStatus = (): ActivityPediatricianStatus | "" => {
        if (joinAnswer === "joined") return "joined";
        if (joinAnswer === "not_joined" || joinAnswer === "unsure") {
            if (willAnswer === "interested") return "interested";
            if (willAnswer === "not_interested") return "not_interested";
        }
        return "";
    };

    const validate = (): string | null => {
        if (!identity) return "請選擇身分";
        if (identity === "孕期中" && (!dueYear || !dueMonth)) return "請填寫預產期";
        if (identity === "家長") {
            if (!babyYear || !babyMonth) return "請填寫寶寶出生年月";
        }
        if (!joinAnswer) return "請選擇是否已加入幼兒專責醫師";
        if (joinAnswer === "joined" && !clinic?.name) return "請選擇或輸入專責診所";
        if ((joinAnswer === "not_joined" || joinAnswer === "unsure") && !willAnswer)
            return "請選擇是否願意由平台協助";
        if (!proofImageBase64) return "請上傳證明照片";
        return null;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const err = validate();
        if (err) {
            alert(err);
            return;
        }
        if (!liffClientObject || !accessToken) {
            alert("登入資訊取得失敗，請重新整理再試");
            return;
        }
        setSubmitting(true);
        const status = derivePediatricianStatus();
        try {
            const res = await submitActivity({
                accessToken,
                identity,
                dueDate: identity === "孕期中" ? `${dueYear}-${dueMonth}` : undefined,
                babyBirth: identity === "家長" ? `${babyYear}-${babyMonth}` : undefined,
                pediatricianStatus: status || undefined,
                pediatricianClinic: status === "joined" ? clinic : null,
                proofImageBase64: proofImageBase64 || undefined,
                proofImageMime: proofImageMime || undefined,
                formData: {
                    identity,
                    dueDate: identity === "孕期中" ? `${dueYear}-${dueMonth}` : "",
                    babyBirth: identity === "家長" ? `${babyYear}-${babyMonth}` : "",
                    joinAnswer,
                    willAnswer,
                },
            });
            if (res?.status_code === 200) {
                setDone(true);
            } else {
                alert(res?.data?.message || "送出失敗，請稍後再試");
            }
        } catch (error) {
            console.error("submit activity error", error);
            alert("因網路問題，無法送出，請稍後再試");
        } finally {
            setSubmitting(false);
        }
    };

    const yearNow = new Date().getFullYear();
    const babyYears = Array.from({ length: 5 }, (_, i) => String(yearNow - i));
    const dueYears = [String(yearNow), String(yearNow + 1)];
    const months = Array.from({ length: 12 }, (_, i) => String(i + 1));

    const labelBold: React.CSSProperties = { color: "#555555", fontSize: "21px", fontWeight: "bold" };
    const radioLabel: React.CSSProperties = { color: "#555555", fontSize: "18px", lineHeight: "1.9" };
    const sampleImgStyle: React.CSSProperties = { maxWidth: "100%", borderRadius: "6px", border: "1px solid #e0e0e0" };
    const ymSelectStyle: React.CSSProperties = { fontSize: "18px", padding: "6px 10px", borderRadius: "6px", border: "1px solid #cccccc", color: "#555555", minWidth: "84px" };
    const ymUnitStyle: React.CSSProperties = { fontSize: "18px", color: "#555555" };

    if (done) {
        return (
            <RootLayout title="幼兒專責醫師與你童行">
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "60px 24px", gap: "20px" }}>
                    <div style={{ fontSize: "48px" }}>🎉</div>
                    <div style={{ color: "#555555", fontSize: "22px", fontWeight: "bold" }}>已填寫完成</div>
                    <div style={{ color: "#777777", fontSize: "16px" }}>感謝您的參與</div>
                    <SubmitButton
                        type="button"
                        className="profileSubmitbtn"
                        text="關閉畫面"
                        onClick={() => liff.closeWindow()}
                    />
                </div>
            </RootLayout>
        );
    }

    return (
        <RootLayout title="幼兒專責醫師與你童行">
            <form onSubmit={handleSubmit}>
                <div style={{ display: "flex", flexDirection: "column", padding: "20px 44px", gap: "16px" }}>
                    {/* 活動標題 */}
                    <div style={{ fontSize: "22px", fontWeight: "bold", color: "#C83862", textAlign: "center" }}>
                        有獎徵答活動 🎁
                    </div>

                    {/* 區塊標題 */}
                    <div style={{ fontSize: "18px", fontWeight: "bold", color: "#9F0050", borderBottom: "2px solid #F3C5D5", paddingBottom: "6px" }}>
                        【資格審核與基本資料】
                    </div>

                    {/* 身分 */}
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        <label style={labelBold}>請問您目前的身分為？<span style={{ color: "#e53935" }}> *</span></label>
                        {(["孕期中", "家長", "其他"] as Identity[]).map((opt) => (
                            <label key={opt} style={radioLabel}>
                                <input
                                    type="radio"
                                    name="identity"
                                    checked={identity === opt}
                                    onChange={() => setIdentity(opt)}
                                    disabled={loading || submitting}
                                />
                                &nbsp;{opt === "家長" ? "0–3歲幼兒之家長" : opt}
                            </label>
                        ))}
                    </div>

                    {/* 預產期（孕期中） */}
                    {identity === "孕期中" && (
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                            <label style={labelBold}>預產期<span style={{ color: "#e53935" }}> *</span></label>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                                <select style={ymSelectStyle} value={dueYear} disabled={submitting} required
                                    onChange={(e) => setDueYear(e.target.value)}>
                                    <option value="">請選擇</option>
                                    {dueYears.map((y) => <option key={y} value={y}>{y}</option>)}
                                </select>
                                <span style={ymUnitStyle}>年</span>
                                <select style={ymSelectStyle} value={dueMonth} disabled={submitting} required
                                    onChange={(e) => setDueMonth(e.target.value)}>
                                    <option value="">請選擇</option>
                                    {months.map((m) => <option key={m} value={m}>{m}</option>)}
                                </select>
                                <span style={ymUnitStyle}>月</span>
                            </div>
                        </div>
                    )}

                    {/* 寶寶出生年月（家長；用於關聯基本資料的同一小孩） */}
                    {identity === "家長" && (
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                            <label style={labelBold}>寶寶出生年月<span style={{ color: "#e53935" }}> *</span></label>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                                <select style={ymSelectStyle} value={babyYear} disabled={submitting} required
                                    onChange={(e) => setBabyYear(e.target.value)}>
                                    <option value="">請選擇</option>
                                    {babyYears.map((y) => <option key={y} value={y}>{y}</option>)}
                                </select>
                                <span style={ymUnitStyle}>年</span>
                                <select style={ymSelectStyle} value={babyMonth} disabled={submitting} required
                                    onChange={(e) => setBabyMonth(e.target.value)}>
                                    <option value="">請選擇</option>
                                    {months.map((m) => <option key={m} value={m}>{m}</option>)}
                                </select>
                                <span style={ymUnitStyle}>月</span>
                            </div>
                        </div>
                    )}

                    {/* 上傳證明照片 */}
                    {identity && (
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                            <label style={labelBold}>請上傳相關證明照片<span style={{ color: "#e53935" }}> *</span></label>
                            {(identity === "孕期中" || identity === "其他") && (
                                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                                    <div style={{ color: "#444444", fontSize: "17px" }}>
                                        孕婦：請上傳「孕婦健康手冊」產前檢查紀錄頁面
                                    </div>
                                    <div style={{ color: "#888888", fontSize: "14px" }}>範例（示意，請上傳您本人的手冊頁面）：</div>
                                    <img src="/img/activity-sample-pregnant-1.png" alt="孕婦健康手冊範例1" style={sampleImgStyle} />
                                    <img src="/img/activity-sample-pregnant-2.png" alt="孕婦健康手冊範例2" style={sampleImgStyle} />
                                </div>
                            )}
                            {(identity === "家長" || identity === "其他") && (
                                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                                    <div style={{ color: "#444444", fontSize: "17px" }}>
                                        家長：請上傳「兒童健康手冊」之預防接種時程及紀錄頁面
                                    </div>
                                    <div style={{ color: "#888888", fontSize: "14px" }}>範例（示意，請上傳您本人的手冊頁面）：</div>
                                    <img src="/img/activity-sample-child.png" alt="兒童健康手冊接種紀錄範例" style={sampleImgStyle} />
                                </div>
                            )}
                            <div style={{ color: "#b45309", fontSize: "16px", fontWeight: 500 }}>
                                資料僅供活動資格確認，建議可遮蓋或刪去個人隱私資料後再上傳。
                            </div>
                            <input type="file" accept="image/*" onChange={handleImage} disabled={submitting} />
                            {proofPreview && (
                                <img src={proofPreview} alt="預覽"
                                    style={{ maxWidth: "100%", borderRadius: "6px", border: "1px solid #e0e0e0" }} />
                            )}
                        </div>
                    )}

                    {/* 幼兒專責醫師（所有身分皆需填寫） */}
                    {identity && (
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                            <label style={labelBold}>目前是否已加入幼兒專責醫師制度？<span style={{ color: "#e53935" }}> *</span></label>
                            <label style={radioLabel}>
                                <input type="radio" name="join" checked={joinAnswer === "joined"}
                                    onChange={() => handleJoin("joined")} disabled={submitting} />
                                &nbsp;已加入
                            </label>
                            <label style={radioLabel}>
                                <input type="radio" name="join" checked={joinAnswer === "not_joined"}
                                    onChange={() => handleJoin("not_joined")} disabled={submitting} />
                                &nbsp;尚未加入
                            </label>
                            <label style={radioLabel}>
                                <input type="radio" name="join" checked={joinAnswer === "unsure"}
                                    onChange={() => handleJoin("unsure")} disabled={submitting} />
                                &nbsp;不確定是否已加入
                            </label>

                            {joinAnswer === "joined" && (
                                <div style={{ marginTop: "4px" }}>
                                    <div style={{ ...radioLabel, marginBottom: "4px" }}>目前加入哪一間診所／醫院？<span style={{ color: "#e53935" }}> *</span></div>
                                    <CreatableSelect
                                        isClearable
                                        placeholder="請搜尋診所文字"
                                        formatCreateLabel={(input) => `使用自由輸入：「${input}」`}
                                        noOptionsMessage={() => "查無診所，可直接輸入名稱"}
                                        isDisabled={submitting}
                                        options={childCareClinics}
                                        value={clinic ? { value: clinic.code, label: clinic.name } : null}
                                        onChange={(option, meta) => handleClinicChange(option, meta)}
                                        styles={{
                                            control: (base) => ({ ...base, fontSize: "16px" }),
                                            menu: (base) => ({ ...base, fontSize: "16px" }),
                                            placeholder: (base) => ({ ...base, fontSize: "16px" }),
                                        }}
                                    />
                                    <div style={{ color: "#888888", fontSize: "14px", marginTop: "4px" }}>
                                        找不到診所？可直接輸入診所／醫院名稱，再點選「使用自由輸入」。
                                    </div>
                                </div>
                            )}

                            {(joinAnswer === "not_joined" || joinAnswer === "unsure") && (
                                <div style={{ marginTop: "4px", display: "flex", flexDirection: "column", gap: "8px" }}>
                                    <div style={radioLabel}>是否願意由平台協助了解幼兒專責醫師制度或協助媒合？<span style={{ color: "#e53935" }}> *</span></div>
                                    <label style={radioLabel}>
                                        <input type="radio" name="will" checked={willAnswer === "interested"}
                                            onChange={() => setWillAnswer("interested")} disabled={submitting} />
                                        &nbsp;有興趣加入／協助確認
                                    </label>
                                    <label style={radioLabel}>
                                        <input type="radio" name="will" checked={willAnswer === "not_interested"}
                                            onChange={() => setWillAnswer("not_interested")} disabled={submitting} />
                                        &nbsp;暫時沒有需求
                                    </label>
                                </div>
                            )}
                        </div>
                    )}

                    <SubmitButton className="profileSubmitbtn" text="送出" loading={submitting} disabled={submitting} />
                </div>
            </form>

            <Dialog open={loading || submitting}>
                <DialogTitle>{submitting ? "送出中..." : "資料載入中..."}</DialogTitle>
            </Dialog>
        </RootLayout>
    );
};

export default Activity;
